import { useEffect, useState } from 'react';
import { Alert, Box, TextField } from '@mui/material';
import Modal from '../../../components/atoms/modal/modal';
import Button from '../../../components/atoms/button';
import { CreateToBackoffice } from '../../../utils/requests';

type CreateWmiProps = {
  brandId: number | '';
  brandName: string;
  open: boolean;
  onClose: () => void;
  onCreated: (code: string) => void;
  initialCode?: string;
};

type CreatedWmi = {
  code?: string;
};

const CreateWmi = ({ brandId, brandName, open, onClose, onCreated, initialCode }: CreateWmiProps) => {
  const [code, setCode] = useState(initialCode ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setCode(initialCode ?? '');
      setError(null);
    }
  }, [open, initialCode]);

  const handleSave = async () => {
    const normalizedCode = code.trim().toUpperCase();
    if (brandId === '' || !normalizedCode) {
      return;
    }

    if (!/^[A-Z0-9]{3}$/.test(code.trim())) {
      setError('WMI code must be exactly 3 uppercase letters or numbers.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const created = await CreateToBackoffice<CreatedWmi | string | undefined>(
        `portal/vehicle/brands/${brandId}/wmis`,
        { code: normalizedCode }
      );
      const createdCode = typeof created === 'string' ? created : created?.code;
      onCreated(createdCode ?? normalizedCode);
      onClose();
    } catch (saveError) {
      console.error('Error creating brand WMI', saveError);
      setError('Unable to create brand WMI.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add brand WMI"
      footer={
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || brandId === '' || !code.trim()}>Save</Button>
        </Box>
      }
    >
      {error && <Alert severity="error">{error}</Alert>}
      <TextField
        label="Brand"
        value={brandName}
        disabled
        size="small"
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="WMI code"
        value={code}
        onChange={(event) => setCode(event.target.value)}
        size="small"
        fullWidth
        inputProps={{ maxLength: 3 }}
        autoFocus
      />
    </Modal>
  );
};

export default CreateWmi;
